import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../components/primitives/data-display/DataSequence";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import { gaps } from "../../../components/primitives/spacing";
import { DocumentLink } from "../../../components/domain/data-display/DocumentLink";
import { TierClearedBadges } from "../../../components/domain/data-display/TierClearedBadges";
import type { QueueItem } from "../../../lib/corpusApi";
import { useImproveRankingList } from "../ImproveRanking/useImproveRankingList";
import { ROUTES } from "../../../route_paths";

const COLUMNS: DataSequenceColumn<QueueItem>[] = [
  {
    key: "title",
    header: "Title",
    width: 280,
    cardRole: "primary",
    cell: (doc) => <DocumentLink title={doc.title ?? doc.url} url={doc.url} />,
  },
  {
    key: "rank_stability",
    header: "Rank stability",
    cell: (doc) => (doc.rank_stability !== null ? `${Math.round(doc.rank_stability * 100)}%` : "—"),
  },
  {
    key: "tier_cleared",
    header: "Tier cleared",
    cell: (doc) => (
      <TierClearedBadges status={doc.tier_cleared} confidenceScores={doc.confidence_scores} />
    ),
  },
  {
    key: "action",
    header: "",
    cardRole: "action",
    cell: (doc) => (
      <Button
        component={Link}
        to={ROUTES.internal.annotation.improveRanking.path({ id: doc.id })}
        size="small"
        variant="outlined"
      >
        Improve
      </Button>
    ),
    align: "right",
  },
];

/** Ingested documents, the least stably ranked first, each leading to its Improve Ranking page.
 *  Loaded only once its tab is open. Optional, low-priority work: a read of the same corpus, not a
 *  distinct workflow. */
export function ImproveRankingPanel() {
  const { documents, loading, error, pagination } = useImproveRankingList();

  return (
    <Stack gap={gaps.base}>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={COLUMNS}
          rows={documents}
          getRowId={(doc) => doc.id}
          emptyMessage="No ingested documents to improve."
          pagination={pagination}
        />
      )}
    </Stack>
  );
}
